/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

/** The four SOAP sections (Figma "Session Notes"). Empty is allowed for drafts. */
export const SoapSchema = z.object({
  subjective: z.string().max(5000),
  objective: z.string().max(5000),
  assessment: z.string().max(5000),
  plan: z.string().max(5000),
});
export type SoapInput = z.infer<typeof SoapSchema>;

/** Per-goal trial counts captured during a session (BRD M03). */
export const TrialEntrySchema = z.object({
  goalId: z.string().uuid(),
  correct: z.number().int().min(0).max(1000),
  incorrect: z.number().int().min(0).max(1000),
  prompted: z.number().int().min(0).max(1000),
  note: z.string().trim().max(500).optional(),
});
export type TrialEntryInput = z.infer<typeof TrialEntrySchema>;

/** Upsert the draft SOAP note + trial data for a session. Mirrors `SaveNoteDto`. */
export const SaveNoteSchema = z.object({
  soap: SoapSchema,
  trials: z.array(TrialEntrySchema).max(50).optional(),
});
export type SaveNoteInput = z.infer<typeof SaveNoteSchema>;

/** Optional ISO calendar date (yyyy-mm-dd) from a date input; '' means unset. */
const OptionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
  .optional()
  .or(z.literal(''));

/** A teaching step beneath a sub-target — the leaf tier (BRD M01 #3). Mirrors `StepItemInputDto`. */
export const StepItemInputSchema = z.object({
  title: z.string().trim().min(2, 'Step is too short').max(200),
  done: z.boolean().optional(),
});
export type StepItemInput = z.infer<typeof StepItemInputSchema>;

/** A sub-target beneath a goal (P1-15), with its teaching steps. Mirrors `StepInputDto`. */
export const StepInputSchema = z.object({
  title: z.string().trim().min(2, 'Sub-target is too short').max(200),
  done: z.boolean().optional(),
  items: z.array(StepItemInputSchema).max(20).optional(),
});
export type StepInput = z.infer<typeof StepInputSchema>;

/** Therapy discipline a goal belongs to (BRD M01 goal taxonomy). Mirrors the DB enum. */
export const GOAL_DOMAINS = ['SLT', 'OT', 'ABA', 'PT', 'SPECED'] as const;
export type GoalDomain = (typeof GOAL_DOMAINS)[number];

/**
 * Standardised measurement method for a goal (BRD M01 #4). Mirrors the DB
 * `itp_measurement_type` enum; `measurementMethod` carries the free-text detail.
 */
export const MEASUREMENT_TYPES = [
  'frequency',
  'percent_accuracy',
  'duration',
  'latency',
  'trials_to_criterion',
  'rating_scale',
  'independence_level',
  'prompt_level',
  'anecdotal',
] as const;
export type MeasurementType = (typeof MEASUREMENT_TYPES)[number];

/** One micro-level goal in an ITP (Figma "ITP Builder"). Mirrors `GoalInputDto`. */
export const GoalInputSchema = z.object({
  title: z.string().trim().min(2, 'Goal is too short').max(200),
  category: z.string().trim().max(120).optional().or(z.literal('')),
  domain: z.enum(GOAL_DOMAINS).optional(),
  measurementType: z.enum(MEASUREMENT_TYPES).optional(),
  measurementMethod: z.string().trim().max(200).optional().or(z.literal('')),
  baseline: z.string().trim().max(200).optional().or(z.literal('')),
  target: z.string().trim().max(200).optional().or(z.literal('')),
  reviewDate: OptionalDate,
  taxonomyId: z.string().uuid().optional(),
  approachTags: z.array(z.string().trim().min(1).max(40)).max(10).optional(),
  steps: z.array(StepInputSchema).max(20).optional(),
});
export type GoalInput = z.infer<typeof GoalInputSchema>;

/** Create a therapy plan (version 1) with its goal set. Mirrors `CreateItpDto`. */
export const CreateItpSchema = z.object({
  clientId: z.string().uuid('Select a client'),
  name: z.string().trim().min(2, 'Plan name is too short').max(160),
  programType: z.string().trim().max(160).optional().or(z.literal('')),
  startDate: OptionalDate,
  reviewDate: OptionalDate,
  note: z.string().trim().max(500).optional().or(z.literal('')),
  goals: z.array(GoalInputSchema).min(1, 'Add at least one goal').max(50),
});
export type CreateItpInput = z.infer<typeof CreateItpSchema>;

/** Outcome of a clinical review (BRD M01 review workflow). Mirrors the DB enum. */
export const REVIEW_OUTCOMES = ['continue', 'revise', 'discharge'] as const;
export type ReviewOutcome = (typeof REVIEW_OUTCOMES)[number];

/**
 * Record a periodic (≈3-month) clinical review on a plan (BRD M01). A summary is
 * always required; a next review date is required when the plan continues or is
 * revised, and omitted on discharge (the plan completes). Mirrors `RecordReviewDto`.
 */
export const RecordReviewSchema = z
  .object({
    outcome: z.enum(REVIEW_OUTCOMES),
    summary: z.string().trim().min(2, 'Add a review summary').max(4000),
    recommendations: z.string().trim().max(4000).optional().or(z.literal('')),
    nextReviewDate: OptionalDate,
  })
  .superRefine((value, ctx) => {
    if (
      value.outcome !== 'discharge' &&
      (value.nextReviewDate === undefined || value.nextReviewDate === '')
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nextReviewDate'],
        message: 'Set the next review date',
      });
    }
  });
export type RecordReviewInput = z.infer<typeof RecordReviewSchema>;

/** Save a reusable plan template from a goal set (P1-15b). Mirrors `CreateTemplateDto`. */
export const CreateTemplateSchema = z.object({
  name: z.string().trim().min(2, 'Template name is too short').max(160),
  programType: z.string().trim().max(160).optional().or(z.literal('')),
  description: z.string().trim().max(500).optional().or(z.literal('')),
  goals: z.array(GoalInputSchema).min(1, 'Add at least one goal').max(50),
});
export type CreateTemplateInput = z.infer<typeof CreateTemplateSchema>;
